/**
 * NavIcon — the ONE nav-glyph renderer, shared by the sidebar/rail (AppShell)
 * AND the on-page form title (<FormTitle>), so the icon shown before a form's
 * name is ALWAYS the exact glyph its navigation entry shows (same emoji or
 * line icon, same colour).  Moved out of AppShell 2026-07-03 precisely so the
 * two render paths cannot drift (user request: "the form-title icon must
 * match the navigation icon and colour").
 *
 * Rendering rules (identical to the old AppShell-local NavIcon):
 *  - a `ti-*` string mapped in TI_EMOJI renders as its colourful emoji;
 *  - an unmapped `ti-*` renders as its crisp Tabler line glyph, coloured by
 *    the optional inline `color` (else the iconColors.css palette);
 *  - an already-emoji string renders as text directly.
 */

/** Tabler-icon → emoji map — the sidebar/rail's colourful glyph set (exact
 *  emoji chosen by the user, 2026-06).  Any `ti-*` not listed falls back to
 *  its line glyph. */
export const TI_EMOJI: Record<string, string> = {
  // Overview / modules
  'ti-home': '🏠', 'ti-layout-dashboard': '📊', 'ti-sparkles': '✨', 'ti-chart-histogram': '📈',
  'ti-brain': '🧠', 'ti-layout-grid': '🧩',
  // Task Tracker
  'ti-calendar-time': '🗓️', 'ti-calendar-stats': '📆', 'ti-progress': '📊', 'ti-progress-check': '☑️',
  // Sample Backlog / ICM
  'ti-stack': '🗂️', 'ti-alert-circle': '⚠️', 'ti-clock': '⏰', 'ti-table': '📋',
  'ti-circle-check': '✔️', 'ti-shopping-cart': '🛒', 'ti-shield-check': '🛡️', 'ti-receipt': '🧾',
  'ti-certificate': '📜',
  // Sampling lifecycle
  'ti-calendar': '📅', 'ti-grid-dots': '🔳', 'ti-map-pin': '📍', 'ti-clipboard-list': '📋',
  'ti-bell': '🔔', 'ti-map-2': '🗺️', 'ti-arrows-exchange': '🔁', 'ti-transfer': '🔄',
  'ti-file-invoice': '🧾', 'ti-send': '📤', 'ti-test-pipe': '⚗️', 'ti-packages': '📦',
  // Analysis / Data Review
  'ti-microscope': '🔬', 'ti-chart-dots': '📉', 'ti-list-check': '☑️', 'ti-checkup-list': '✅',
  // Reporting
  'ti-report': '📄', 'ti-file-analytics': '📈', 'ti-file-check': '📑', 'ti-file-export': '📤',
  'ti-files': '🗂️', 'ti-database-export': '💾', 'ti-report-medical': '🩺', 'ti-clock-check': '⏱️',
  'ti-truck-delivery': '🚚', 'ti-archive': '🗃️', 'ti-report-analytics': '📊',
  // QA/QC + System
  'ti-chart-line': '📈', 'ti-server-2': '🖥️', 'ti-server': '🖥️', 'ti-mail-cog': '📧',
  // Settings
  'ti-settings': '⚙️', 'ti-category': '🗂️', 'ti-category-2': '🗂️', 'ti-settings-cog': '⚙️',
  'ti-forms': '📝', 'ti-user-check': '👤', 'ti-calendar-event': '📅', 'ti-cash': '💵',
  'ti-calculator': '🧮', 'ti-adjustments-cog': '🎛️', 'ti-settings-2': '⚙️', 'ti-adjustments': '🎛️',
  // User & Security
  'ti-shield-lock': '🔒', 'ti-user-circle': '👤', 'ti-users': '👥', 'ti-id-badge-2': '🪪',
  'ti-lock-cog': '🔐', 'ti-table-options': '🗃️', 'ti-lock-access': '🔐',
  // Data Center
  'ti-database': '🗄️', 'ti-users-group': '👥', 'ti-message-report': '💬', 'ti-address-book': '📇',
  'ti-folder': '📁', 'ti-building': '🏢', 'ti-history': '🕘', 'ti-receipt-2': '🧾', 'ti-box': '📦',
  'ti-id': '🪪', 'ti-notes': '📝', 'ti-login': '🔑', 'ti-list-details': '📋', 'ti-flask': '🧫',
  'ti-alert-octagon': '🛑', 'ti-books': '📚', 'ti-clipboard-text': '📋', 'ti-file-dollar': '💵',
  'ti-flask-2': '🧫', 'ti-list-numbers': '🔢', 'ti-template': '📐', 'ti-components': '🧩',
  'ti-box-multiple': '📦', 'ti-currency-dollar': '💲', 'ti-hourglass': '⏳',
  'ti-device-desktop-analytics': '🖥️', 'ti-test-pipe-2': '⚗️', 'ti-history-toggle': '🕘',
  'ti-lock-access2': '🔐', 'ti-truck': '🚚', 'ti-file-text': '📄', 'ti-clipboard-check': '🛠️',
  // Previously-unmapped ti-* (were rendering as Tabler line icons) — now emoji so the
  // whole nav is emoji-only + consistent on every machine (2026-07-15, user request).
  'ti-adjustments-alt': '🎛️', 'ti-adjustments-horizontal': '🎚️', 'ti-arrows-horizontal': '↔️',
  'ti-book': '📖', 'ti-building-community': '🏘️', 'ti-chart-dots-3': '📊', 'ti-check': '✔️',
  'ti-checkbox': '☑️', 'ti-clipboard-data': '📋', 'ti-clock-hour-4': '🕓', 'ti-droplet': '💧',
  'ti-file-import': '📥', 'ti-flag': '🚩', 'ti-flask-off': '🚱', 'ti-hierarchy-2': '🌳',
  'ti-language': '🌐', 'ti-line': '➖', 'ti-line-dashed': '〰️', 'ti-list-tree': '🌲',
  'ti-mail': '📧', 'ti-mail-fast': '📨', 'ti-mail-forward': '📤', 'ti-message-2': '💬',
  'ti-puzzle': '🧩', 'ti-ruler-2': '📏', 'ti-server-cog': '🖥️', 'ti-shield-half': '🛡️',
  'ti-sitemap': '🗺️', 'ti-source-code': '💻', 'ti-tags': '🏷️', 'ti-temperature': '🌡️',
  'ti-user-cog': '👤',
};

/** Render a nav icon.  Any `ti-*` string maps to a colourful EMOJI (TI_EMOJI)
 *  so the whole sidebar/rail — and every form title — is consistent; an
 *  already-emoji string renders as text directly.  Unmapped `ti-*` falls back
 *  to its line glyph. */
export function NavIcon({ icon, color, cls }: { icon: string; color?: string; cls?: string }) {
  if (!icon) return null;
  if (icon.startsWith('ti-')) {
    const emoji = TI_EMOJI[icon];
    if (emoji) return <span className={`nav-emoji${cls ? ` ${cls}` : ''}`}>{emoji}</span>;
    return <i className={`ti ${icon}${cls ? ` ${cls}` : ''}`} style={color ? { color } : undefined} />;
  }
  return <span className={`nav-emoji${cls ? ` ${cls}` : ''}`}>{icon}</span>;
}
